import { Logger } from 'pino';

import { isServerside } from '../../core/utils/isServerside';
import { encodeQueryString, QueryParams } from '../../utils/encodeQueryString';
import ServerMetrics, { RequestType } from '../../wss/utils/ServerMetrics';

type FailedApiResponse = {
  ok: false;
  status: number;
  detail: string;
};

type SuccessApiResponse<T> = {
  ok: true;
  status: number;
  headers: Headers;
  data: T;
};

export type ApiResponse<T> = FailedApiResponse | SuccessApiResponse<T>;

const buildUrl = (url: string, params?: QueryParams): string =>
  `${url}${encodeQueryString(params)}`;

const defaultServersideHeaders = isServerside()
  ? { 'User-Agent': `FIE widgets server/${process.env.VERSION} (Node.js ${process.version})` }
  : undefined;

export const getJSON = async <T>(
  url: string,
  params?: QueryParams,
  headers?: HeadersInit,
): Promise<ApiResponse<T>> => {
  // Use request timeout only on server
  const maxTime = isServerside() ? parseInt(process.env.API_REQUEST_TIMEOUT ?? '', 10) : NaN;

  try {
    const response = await fetch(buildUrl(url, params), {
      signal: isNaN(maxTime) ? undefined : AbortSignal.timeout(maxTime),
      headers: {
        ...headers,
        ...defaultServersideHeaders,
      },
    });
    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        detail: 'Unknown error while fetching data',
      };
    }
    const data = (await response.json()) as T;
    return {
      ok: true,
      status: response.status,
      headers: response.headers,
      data,
    };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      return {
        ok: false,
        status: 0,
        detail: 'Fetch aborted by user action',
      };
    }
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      return {
        ok: false,
        status: 408,
        detail: 'Request timed out',
      };
    }
    if (e instanceof Error && e.name === 'TypeError') {
      return {
        ok: false,
        status: 500,
        detail: `Error while fetching data. ${e} Cause: ${e.cause}`,
      };
    }
    return {
      ok: false,
      status: 500,
      detail: 'Unknown error while fetching data',
    };
  }
};

export type GetJSON<T> = (url: string, params?: QueryParams) => Promise<ApiResponse<T>>;

export const createGetJSONWithMetrics =
  <T>(metrics: ServerMetrics, logger: Logger, type: RequestType, wssEndpoint: string) =>
  async (url: string, params?: QueryParams): Promise<ApiResponse<T>> => {
    const startTime = Date.now();
    const response = await getJSON<T>(url, params);
    const duration = Date.now() - startTime;

    if (response.ok) {
      metrics.storeRequestTime(type, 'success', wssEndpoint, duration);
    } else {
      logger.error(
        {
          type,
          wssEndpoint,
          url: buildUrl(url, params),
          status: response.status,
          detail: response.detail,
          duration,
        },
        'API request failed',
      );
      const result = response.status === 408 ? 'timeout' : 'error';
      metrics.storeRequestTime(type, result, wssEndpoint, duration);
    }

    return response;
  };

// TODO(Petr): HAWK-7013 Create createPostJSONWithMetrics?
export function postJSON<T>(url: string, data: unknown): Promise<ApiResponse<T>>;
export function postJSON(
  url: string,
  data: unknown,
  responseType: 'text',
): Promise<ApiResponse<string>>;
export async function postJSON<T>(
  url: string,
  data: unknown,
  responseType: 'json' | 'text' = 'json',
): Promise<ApiResponse<T | string>> {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...defaultServersideHeaders,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        detail: 'Unknown error while fetching data',
      };
    }
    const responseData =
      responseType === 'text' ? await response.text() : ((await response.json()) as T);

    return {
      ok: true,
      status: response.status,
      headers: response.headers,
      data: responseData,
    };
  } catch (e) {
    return {
      ok: false,
      status: 500,
      detail: 'Unknown error while fetching data',
    };
  }
}

// TODO(Petr): HAWK-7013 Merge with the other post
export const postForm = async <T>(
  url: string,
  formData: FormData,
  params?: QueryParams,
): Promise<ApiResponse<T>> => {
  try {
    const response = await fetch(buildUrl(url, params), {
      method: 'POST',
      body: formData,
      headers: defaultServersideHeaders,
    });
    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        detail: 'Unknown error while fetching data',
      };
    }
    const responseData = (await response.json()) as T;
    return {
      ok: true,
      status: response.status,
      headers: response.headers,
      data: responseData,
    };
  } catch (e) {
    return {
      ok: false,
      status: 500,
      detail: 'Unknown error while fetching data',
    };
  }
};

export type PostForm<T> = (
  url: string,
  formData: FormData,
  params?: QueryParams,
) => Promise<ApiResponse<T>>;
